import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useMutation, useQueryClient } from 'react-query';
import { spaEntityAPI, formationAPI } from '../../services/api';
import {
  AcademicCapIcon,
  XMarkIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';

const SPA_CERTIFICATIONS_KEY = ['spa', 'certifications'];

const CertificateForm = ({ initialData, isEditing = false, onClose, onSuccess, fullPage = false }) => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  
  // États pour les données
  const [students, setStudents] = useState([]);
  const [trainers, setTrainers] = useState([]);

  // État pour le formulaire
  const [formData, setFormData] = useState({
    studentId: '',
    studentName: '',
    certificateType: 'diploma',
    certificateName: '',
    issueDate: '',
    expirationDate: '',
    score: '',
    instructorId: '',
    instructorName: '',
    remarks: ''
  });

  // État pour l'option "Pas d'expiration"
  const [noExpiry, setNoExpiry] = useState(false);

  // Charger les étudiants et formateurs depuis D1
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      formationAPI.getStudents({ limit: 500, page: 1 }).then((r) => r.items || []),
      spaEntityAPI.list('trainers'),
    ]).then(([studentsData, trainersData]) => {
      if (!cancelled) {
        setStudents(studentsData);
        setTrainers(trainersData || []);
      }
    });
    return () => { cancelled = true; };
  }, []);

  // Initialiser le formulaire avec les données existantes pour l'édition
  useEffect(() => {
    if (initialData && isEditing) {
      const hasExpiry = initialData.expirationDate && initialData.expirationDate !== 'never';
      setNoExpiry(!hasExpiry);
      
      setFormData({
        studentId: initialData.studentId || '',
        studentName: initialData.studentName || '',
        certificateType: initialData.certificateType || 'diploma',
        certificateName: initialData.certificateName || '',
        issueDate: initialData.issueDate || '',
        expirationDate: hasExpiry ? initialData.expirationDate : '',
        score: initialData.score || '',
        instructorId: initialData.instructorId || '',
        instructorName: initialData.instructorName || '',
        remarks: initialData.remarks || ''
      });
    }
  }, [initialData, isEditing]);

  // Mutation pour ajouter une certification
  const addCertificationMutation = useMutation(
    async (newCertification) => {
      const certId = `CERT${Date.now().toString().slice(-4)}`;
      const certification = {
        id: certId,
        ...newCertification,
        status: newCertification.issueDate ? 'issued' : 'pending',
      };
      const saved = await spaEntityAPI.create('certifications', certification);
      return { message: 'Certification ajoutée avec succès', certification: saved };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(SPA_CERTIFICATIONS_KEY);
        onSuccess && onSuccess();
      },
    }
  );

  // Mutation pour modifier une certification
  const updateCertificationMutation = useMutation(
    async (updatedCertification) => {
      const certification = {
        ...initialData,
        ...updatedCertification,
        status: updatedCertification.issueDate ? 'issued' : 'pending',
      };
      const saved = await spaEntityAPI.update('certifications', initialData.id, certification);
      return { message: 'Certification mise à jour avec succès', certification: saved };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(SPA_CERTIFICATIONS_KEY);
        onSuccess && onSuccess();
      },
    }
  );

  // Gestion du formulaire
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    
    // Si on change l'étudiant, mettre à jour l'ID et le nom
    if (name === 'studentId') {
      const selectedStudent = students.find(s => s.id === value);
      setFormData(prev => ({
        ...prev,
        studentId: value,
        studentName: selectedStudent ? selectedStudent.name : ''
      }));
      return;
    }
    
    // Si on change le formateur, mettre à jour l'ID et le nom
    if (name === 'instructorId') {
      const selectedTrainer = trainers.find((tr) => tr.id === value);
      setFormData(prev => ({
        ...prev,
        instructorId: value,
        instructorName: selectedTrainer ? selectedTrainer.name : ''
      }));
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Gérer la soumission du formulaire
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Préparer les données avec la gestion de l'expiration
    const submitData = {
      ...formData,
      expirationDate: noExpiry ? 'never' : formData.expirationDate
    };
    
    if (isEditing && initialData) {
      updateCertificationMutation.mutate(submitData);
    } else {
      addCertificationMutation.mutate(submitData);
    }
  };

  // Vérifier si une mutation est en cours
  const isLoading = addCertificationMutation.isLoading || updateCertificationMutation.isLoading;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900 rounded-2xl p-6 w-full"
    >
      <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-6 flex items-center">
        <AcademicCapIcon className="h-6 w-6 ltr:mr-2 rtl:ml-2" />
        {isEditing ? t('certificationsComponents.formTitleEdit') : t('certificationsComponents.formTitleNew')}
      </h2>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Informations de l'étudiant */}
          <div className="md:col-span-2">
            <label className="block text-slate-600 dark:text-slate-400 text-sm mb-1">{t('certificationsComponents.studentNameLbl')}</label>
            <select
              name="studentId"
              value={formData.studentId}
              onChange={handleFormChange}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">{t('certificationsComponents.selectStudent')}</option>
              {students.map(student => (
                <option key={student.id} value={student.id}>
                  {student.name} ({student.studentCode})
                </option>
              ))}
            </select>
          </div>
          
          {/* Type de certification */}
          <div>
            <label className="block text-slate-600 dark:text-slate-400 text-sm mb-1">{t('certificationsComponents.certificateTypeLbl')}</label>
            <select
              name="certificateType"
              value={formData.certificateType}
              onChange={handleFormChange}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-50"
              required
            >
              <option value="diploma">{t('certificationsDetail.typeDiploma')}</option>
              <option value="certification">{t('certificationsDetail.typeCertificationShort')}</option>
              <option value="attestation">{t('certificationsDetail.typeAttestation')}</option>
            </select>
          </div>
          
          {/* Nom de la certification */}
          <div>
            <label className="block text-slate-600 dark:text-slate-400 text-sm mb-1">{t('certificationsComponents.certificateTitleLbl')}</label>
            <input 
              type="text"
              name="certificateName"
              value={formData.certificateName}
              onChange={handleFormChange}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-50"
              required
            />
          </div>
          
          {/* Dates */}
          <div>
            <label className="block text-slate-600 dark:text-slate-400 text-sm mb-1">{t('certificationsComponents.issueDateFieldLbl')}</label>
            <input 
              type="date"
              name="issueDate"
              value={formData.issueDate}
              onChange={handleFormChange}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-50"
            />
          </div>
          
          <div>
            <label className="block text-slate-600 dark:text-slate-400 text-sm mb-1">{t('certificationsComponents.expirationFieldLbl')}</label>
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="noExpiry"
                  checked={noExpiry}
                  onChange={(e) => {
                    setNoExpiry(e.target.checked);
                    if (e.target.checked) {
                      setFormData(prev => ({ ...prev, expirationDate: '' }));
                    }
                  }}
                  className="w-4 h-4 text-blue-600 bg-slate-50 dark:bg-slate-800/80 border-slate-200 dark:border-slate-600 rounded focus:ring-blue-500 focus:ring-2"
                />
                <label htmlFor="noExpiry" className="ml-2 text-sm text-slate-700 dark:text-slate-300">
                  {t('certificationsComponents.noExpiryCheckbox')}
                </label>
              </div>
              {!noExpiry && (
                <input 
                  type="date"
                  name="expirationDate"
                  value={formData.expirationDate}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              )}
              {noExpiry && (
                <div className="w-full px-3 py-2 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-sm">
                  {t('certificationsComponents.noExpiryBadge')}
                </div>
              )}
            </div>
          </div>
          
          {/* Score */}
          <div>
            <label className="block text-slate-600 dark:text-slate-400 text-sm mb-1">{t('certificationsComponents.scoreFieldLbl')}</label>
            <input 
              type="number"
              name="score"
              value={formData.score}
              onChange={handleFormChange}
              min="0"
              max="100"
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-50"
            />
          </div>
          
          {/* Informations du formateur */}
          <div className="md:col-span-2">
            <label className="block text-slate-600 dark:text-slate-400 text-sm mb-1">{t('certificationsComponents.instructorNameLbl')}</label>
            <select
              name="instructorId"
              value={formData.instructorId}
              onChange={handleFormChange}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">{t('certificationsComponents.selectTrainer')}</option>
              {trainers.map(trainer => (
                <option key={trainer.id} value={trainer.id}>
                  {trainer.name} - {trainer.specialty}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Remarques */}
        <div>
          <label className="block text-slate-600 dark:text-slate-400 text-sm mb-1">{t('certificationsDetail.remarksLbl')}</label>
          <textarea
            name="remarks"
            value={formData.remarks}
            onChange={handleFormChange}
            rows="3"
            className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-50"
          ></textarea>
        </div>
        
        <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200 dark:border-slate-600">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-50 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            disabled={isLoading}
          >
            {t('certificationsComponents.cancelBtn')}
          </button>
          
          <button
            type="submit"
            disabled={isLoading}
            className="px-6 py-2 bg-forma-600 text-white rounded-lg shadow-md transition hover:bg-forma-500 flex items-center space-x-2"
          >
            {isLoading && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
            <span>{isEditing ? t('certificationsComponents.submitUpdate') : t('certificationsComponents.submitAdd')}</span>
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default CertificateForm;
